import type { Booth } from '@cibli/shared';

import { useEffect, useRef } from 'react';

import { useNetworkMonitor } from '../contexts/network-monitor-context';

export function useBoothControlSocket(
  booth: Booth | Partial<Booth> | null,
  options?: {
    onFlagsUpdated?: () => void;
    onConfigUpdated?: () => void;
    onJobsUpdated?: () => void;
    onOrgsUpdated?: () => void;
  },
) {
  const { runManualTest, isFullTestRunning, lastFullTest } = useNetworkMonitor();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const onFlagsUpdatedRef = useRef(options?.onFlagsUpdated);
  onFlagsUpdatedRef.current = options?.onFlagsUpdated;
  const onConfigUpdatedRef = useRef(options?.onConfigUpdated);
  onConfigUpdatedRef.current = options?.onConfigUpdated;
  const onJobsUpdatedRef = useRef(options?.onJobsUpdated);
  onJobsUpdatedRef.current = options?.onJobsUpdated;
  const onOrgsUpdatedRef = useRef(options?.onOrgsUpdated);
  onOrgsUpdatedRef.current = options?.onOrgsUpdated;

  // Compute WebSocket URL based on API_URL
  const rawApi = import.meta.env.VITE_API_URL || 'http://localhost:3000';
  const cleanBase = rawApi.replace(/\/api\/?$/, '').replace(/\/$/, '');
  const wsBase = cleanBase.replace(/^http/, 'ws');
  const wsUrl = `${wsBase}/booth-control`;

  const boothId = booth?.id;
  const machineUid = (booth as { machineUid?: string })?.machineUid;

  useEffect(() => {
    if (!boothId) return;

    let isDisposed = false;

    function connect() {
      if (isDisposed) return;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          // Identify this booth
          ws.send(
            JSON.stringify({
              type: 'identify',
              boothId,
              machineUid,
            }),
          );
        };

        ws.onmessage = event => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'run_speedtest') {
              if (!isFullTestRunning) {
                void runManualTest();
              }
            } else if (message.type === 'flags_updated') {
              onFlagsUpdatedRef.current?.();
            } else if (message.type === 'config_updated') {
              // Un réglage de la cabine vient d'être modifié côté Back-Office :
              // on rafraîchit immédiatement les paramètres et les caches.
              onConfigUpdatedRef.current?.();
            } else if (message.type === 'jobs_updated') {
              window.dispatchEvent(new CustomEvent('cibli:jobs-updated', { detail: message.data }));
              onJobsUpdatedRef.current?.();
            } else if (message.type === 'orgs_updated') {
              window.dispatchEvent(new CustomEvent('cibli:orgs-updated', { detail: message.data }));
              onOrgsUpdatedRef.current?.();
            }
          } catch (err) {
            console.warn('[BoothControl] Error parsing message', err);
          }
        };

        ws.onclose = () => {
          if (!isDisposed) {
            reconnectTimeoutRef.current = window.setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        if (!isDisposed) {
          reconnectTimeoutRef.current = window.setTimeout(connect, 5000);
        }
      }
    }

    connect();

    return () => {
      isDisposed = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [boothId, machineUid, wsUrl, runManualTest, isFullTestRunning]);

  // When a full test completes, send the result back over the socket
  useEffect(() => {
    if (
      !lastFullTest ||
      !boothId ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }

    try {
      wsRef.current.send(
        JSON.stringify({
          type: 'speedtest_result',
          boothId,
          machineUid,
          data: {
            latencyMs: lastFullTest.latencyMs,
            downloadMbps: lastFullTest.downloadMbps,
            uploadMbps: lastFullTest.uploadMbps,
            testedAt: lastFullTest.testedAt.toISOString(),
          },
        }),
      );
    } catch (err) {
      console.warn('[BoothControl] Failed to send speedtest result', err);
    }
  }, [lastFullTest, boothId, machineUid]);
}
